import { Aide } from '@/components/ui/aide';
import { useSyncOuEnLigne } from '../context/SyncContext';
import { api } from '../lib/api';
import { fetchAllPages } from '../lib/fetchAllPages';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { clientsAPI, ordersAPI, invoicesAPI, exportAPI, volumeUnitsAPI } from '../lib/api';
import { downloadBlob } from '../lib/download';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from '../components/ui/sonner';
import { Label } from '../components/ui/label';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { remainingToDeliver, nextOrderAction, progressOf } from '../lib/orderProgress';
import { libelle, referenceLisible, texteSur } from '../lib/libelles';
import { arrondiDevise, formatDate, formatDateHeure } from '../lib/formats';
import { messageErreur } from '../lib/erreurs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import {
  cn,
  formatVolume,
  formatCurrency,
  formatCompactCurrency,
  formatCompactVolume,
} from '../lib/utils';
import { PageHeader } from '../components/ui/page-header';
import { BandeauFile } from '../components/business/BandeauFile';
import { LoadingState } from '../components/ui/loading-state';
import { EmptyState } from '../components/ui/empty-state';
import { SectionCard } from '../components/dashboard';
import {
  Download,
  MoreVertical,
  ExternalLink,
  FileText,
  Plus,
  Search,
  ShoppingCart,
  Truck,
} from 'lucide-react';
import CisternInput from '../components/business/CisternInput';
import CamionsAAllouer from '../components/business/CamionsAAllouer';
import ChampsAvances from '../components/ui/champs-avances';
import usePrixPropose, { appliquerPrixPropose } from '../hooks/usePrixPropose';
import { formatVolumeWithUnit, UNIT_CISTERNS } from '../lib/volumeUnits';
import { ONBOARDING_ROUTE } from '../components/onboarding';
import { margeCommande, sommeMontants } from '../lib/montantsFacture';
import { useRafraichirSur } from '../hooks/useRafraichirSur';
import { usePagination } from '../hooks/usePagination';
import { BarrePagination } from '../components/ui/pagination-bar';
import { OrderSupplyFields, champsNature } from '../components/stations/supply/OrderSupplyFields';
import { NATURE_REAPPRO, NATURE_VENTE, estReappro } from '../lib/supplyApi';

// L'état affiché est celui que le serveur calcule (`order.progress.state`:
// à affecter, affectée en partie, affectée, en route, livrée…). Le statut
// enregistré ne sert que pour « Facturée » / « Payée », que l'état calculé
// ne distingue pas, et de repli pour une commande sans avancement.
const orderStatusLabel = (order) => {
  if (['INVOICED', 'PAID'].includes(order?.status)) {
    return libelle('statut_commande', order.status);
  }
  if (order?.progress?.state) return libelle('etat_commande', order.progress.state);
  return libelle('statut_commande', order?.status, 'Brouillon');
};

/**
 * Facture active (non annulée) de chaque commande, la plus récente en cas de
 * doublon historique. Une facture annulée ne compte pas: la commande peut être
 * refacturée.
 */
export const facturesActivesParCommande = (invoices) => {
  const parCommande = new Map();
  for (const invoice of invoices || []) {
    if (!invoice || invoice.status === 'CANCELLED') continue;
    for (const orderId of invoice.order_ids || []) {
      const actuelle = parCommande.get(orderId);
      if (!actuelle || String(invoice.invoice_date || '') > String(actuelle.invoice_date || '')) {
        parCommande.set(orderId, invoice);
      }
    }
  }
  return parCommande;
};

const getTodayDateInput = () => new Date().toLocaleDateString('en-CA');

const generateOrderReference = () => {
  const datePart = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  const randomPart = Math.floor(Math.random() * 9000 + 1000);
  return `ORD-${datePart}-${randomPart}`;
};

const createDefaultOrderForm = () => ({
  client_id: '',
  type: 'SALE',
  // Nature (lot 032) : vente, ou réapprovisionnement d'une station de la société.
  supply_kind: NATURE_VENTE,
  destination_station_id: '',
  destination_tanks: [],
  // Une commande naît en brouillon. Le statut n'est plus saisi à la création:
  // demander à un exploitant de choisir entre « Allouée » et « En transit »
  // avant que quoi que ce soit ne soit parti, c'est lui demander de renseigner
  // un état du logiciel au lieu d'un fait du terrain. Il s'avance ensuite par
  // des actions nommées, sur la ligne de la commande.
  status: 'DRAFT',
  quantity_liters: '',
  quantity_cisterns: null,
  unit_price: '',
  unit_cost: '',
  delivery_location: '',
  requested_delivery_date: getTodayDateInput(),
  reference_number: generateOrderReference(),
});

const createDefaultClientForm = () => ({ name: '', phone: '' });

/**
 * Montant estimé d'une nouvelle vente, comparé au plafond de crédit du client.
 * Arrondi à la devise comme le montant que le serveur enregistrera: le
 * plafond se compare à un vrai montant, pas à des centimes.
 */
export const montantEstimeCommande = (form) => {
  if (form?.type !== 'SALE') return 0;
  const qty = Number(form.quantity_liters || 0);
  const unit = Number(form.unit_price || 0);
  return arrondiDevise(qty * unit, form.currency);
};

// Vrai sous 1024 px: la liste passe en cartes au lieu d'un tableau qui défile.
const useSousDesktop = () => {
  const lire = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia('(max-width: 1023px)').matches;
  const [etroit, setEtroit] = useState(lire);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const requete = window.matchMedia('(max-width: 1023px)');
    const suivre = () => setEtroit(requete.matches);
    suivre();
    requete.addEventListener?.('change', suivre);
    return () => requete.removeEventListener?.('change', suivre);
  }, []);
  return etroit;
};

const OrdersPage = () => {
  // Une facture se génère avec le serveur: entrée inactive sans lui.
  const enLigne = useSyncOuEnLigne().isOnline !== false;
  const etroit = useSousDesktop();
  const location = useLocation();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [urgencyFilter, setUrgencyFilter] = useState('ALL');
  const [isLoading, setIsLoading] = useState(true);
  const [clientsError, setClientsError] = useState(null);
  const [generatingInvoice, setGeneratingInvoice] = useState(null);
  // Factures non annulées par commande (numéro, état de paiement). `null`
  // tant qu'elles n'ont pas pu être lues: on se rabat alors sur le statut.
  const [facturesParCommande, setFacturesParCommande] = useState(null);
  const [savingDelivery, setSavingDelivery] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [showLogModal, setShowLogModal] = useState(false);
  const [closingOrder, setClosingOrder] = useState(null);
  const [closeForm, setCloseForm] = useState({});
  const [closeError, setCloseError] = useState('');
  const [closeBusy, setCloseBusy] = useState(false);
  const openClose = order => {setClosingOrder(order);setCloseError('');setCloseForm({reason:'',billing_basis:order.billing_basis || 'DELIVERED',loaded_liters:order.billing_loaded_liters || '',flat_amount:order.billing_flat_amount || '',idempotency_key:crypto.randomUUID()});};
  const editClose = (name,value) => setCloseForm(old=>({...old,[name]:value,idempotency_key:crypto.randomUUID()}));
  const confirmClose = async () => {
    setCloseBusy(true);setCloseError('');
    try {
      const data={reason:closeForm.reason,billing_basis:closeForm.billing_basis,idempotency_key:closeForm.idempotency_key};
      if(data.billing_basis==='LOADED') data.loaded_liters=Number(closeForm.loaded_liters);
      if(data.billing_basis==='FLAT') data.flat_amount=Number(closeForm.flat_amount);
      await api.post(`/orders/${closingOrder.id}/close-delivery`,data);
      setClosingOrder(null);await loadOrders();
    } catch(error) {setCloseError(messageErreur(error, 'Impossible de clôturer la livraison. Réessayez.'));} finally {setCloseBusy(false);}
  };
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [formData, setFormData] = useState(createDefaultOrderForm);
  // Les camions proposés pendant la saisie, et ceux que l'exploitant retient.
  // `selectionCamions` associe un identifiant de trajet au volume qu'on lui
  // confie: c'est déjà la forme attendue par `allocate-batch`, donc rien n'est
  // à retraduire au moment d'envoyer.
  const [camionsDisponibles, setCamionsDisponibles] = useState([]);
  const [chargementCamions, setChargementCamions] = useState(false);
  const [selectionCamions, setSelectionCamions] = useState({});
  // Créer le client sans quitter la commande. Sinon le parcours du premier
  // jour oblige à fermer, aller dans Clients, revenir, et retrouver sa saisie
  // perdue.
  // Les camions partis livrer dont la commande n'est pas soldée. Le seuil de
  // retard vient de la société: cinq jours par défaut, zéro pour l'éteindre.
  const [aSolder, setASolder] = useState([]);
  const [creationClient, setCreationClient] = useState(false);
  const [nouveauClient, setNouveauClient] = useState(createDefaultClientForm);
  // Ce que la société appelle « une citerne ». Purement d'affichage, sauf dans
  // le champ de saisie qui convertit avant l'appel API.
  const [volumeSettings, setVolumeSettings] = useState(null);
  // Une ligne par camion de la commande: le volume qui lui a été promis, ce
  // qu'il a déjà dépoté, et ce que l'exploitant saisit aujourd'hui.
  const [lignesLivraison, setLignesLivraison] = useState([]);
  const [chargementLignes, setChargementLignes] = useState(false);
  // Deux champs que la fiche client sait remplir — le prix au litre et le lieu
  // de livraison. Dès que l'exploitant y touche, plus rien ne les réécrit: un
  // prix négocié et une livraison exceptionnelle l'emportent toujours sur ce
  // que dit le référentiel.
  const [prixTouche, setPrixTouche] = useState(false);
  const [lieuTouche, setLieuTouche] = useState(false);

  // Les clients étaient chargés dans le même `Promise.all` que les commandes:
  // le moindre échec côté commandes rejetait l'ensemble, `setClients` n'était
  // jamais atteint, et la liste déroulante « Client » de la création restait
  // vide sans qu'aucun message ne l'explique. Les deux lectures sont
  // indépendantes, elles se chargent donc séparément.
  const loadClients = useCallback(async () => {
    try {
      const result = await fetchAllPages((page) => clientsAPI.getAllPaged(page));
      setClients(result.items || []);
      setClientsError(null);
    } catch (err) {
      console.error('Failed to load clients:', err);
      setClientsError(
        err?.response?.status === 403
          ? "Vous n'avez pas accès à la liste des clients."
          : 'Liste des clients indisponible. Réessayez.',
      );
    }
  }, []);

  const loadOrders = useCallback(async ({ silencieux = false } = {}) => {
    if (!silencieux) setIsLoading(true);
    loadClients();
    try {
      // Les totaux, les retards et la recherche portent sur toutes les commandes,
      // pas sur les deux cents premières.
      const ordersResult = await fetchAllPages((params) => ordersAPI.getAllPaged(params));
      setOrders(ordersResult.items || []);
      try {
        const facturesResult = await fetchAllPages((params) => invoicesAPI.getAllPaged(params));
        setFacturesParCommande(facturesActivesParCommande(facturesResult.items || []));
      } catch (err) {
        console.error('Failed to load invoices for orders:', err);
        setFacturesParCommande(null);
      }
      // Chargée à part: une file indisponible ne doit pas vider la liste des
      // commandes, qui est la raison d'être de l'écran.
      ordersAPI
        .getToSettle()
        .then(({ lignes }) => setASolder(lignes))
        .catch(() => setASolder([]));
    } catch (err) {
      console.error('Failed to load orders:', err);
      toast.error('Impossible de charger les commandes. Réessayez.');
    } finally {
      setIsLoading(false);
    }
  }, [loadClients]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  // Une écriture faite ailleurs (autre écran, autre onglet, file hors ligne rejouée) recharge ces chiffres: jamais de compteur figé.
  useRafraichirSur(['commandes', 'clients', 'factures', 'trajets', 'repartition', 'stock'], loadOrders);


  // Le réglage de la société, lu une fois. Il ne conditionne rien: sans lui, le
  // champ retombe sur le défaut produit et la saisie reste possible.
  useEffect(() => {
    volumeUnitsAPI.get().then(setVolumeSettings);
  }, []);

  // Les camions proposés se rafraîchissent pendant la frappe du volume, avec un
  // délai: sans lui, taper « 120000 » lance six requêtes dont cinq sont
  // périmées avant d'arriver. Le classement par couverture dépend du volume
  // saisi, donc on ne peut pas se contenter de charger une fois à l'ouverture.
  useEffect(() => {
    if (!showNewModal) return undefined;
    let abandonne = false;
    setChargementCamions(true);
    const minuterie = setTimeout(async () => {
      const camions = await ordersAPI.getAvailableTrucksForDraft({
        quantityLiters: Number(formData.quantity_liters || 0),
        destination: formData.delivery_location || undefined,
      });
      if (abandonne) return;
      setCamionsDisponibles(camions);
      setChargementCamions(false);
      // Un camion qui disparaît de la liste — reparti, entièrement promis
      // ailleurs — ne doit pas rester coché en silence.
      setSelectionCamions((prev) => {
        const vivants = new Set(camions.map((c) => c.trip_id));
        const suite = {};
        Object.entries(prev).forEach(([tripId, volume]) => {
          if (vivants.has(tripId)) suite[tripId] = volume;
        });
        return suite;
      });
    }, 400);
    return () => {
      abandonne = true;
      clearTimeout(minuterie);
    };
  }, [showNewModal, formData.quantity_liters, formData.delivery_location]);

  const STORAGE_PREFIX = 'app_v1_';

  useEffect(() => {
    const raw = localStorage.getItem(STORAGE_PREFIX + 'ordersFilters') || localStorage.getItem('ordersFilters') || '{}';
    const saved = JSON.parse(raw);
    if (saved.searchQuery) setSearchQuery(saved.searchQuery);
    if (saved.statusFilter) setStatusFilter(saved.statusFilter);
    if (saved.typeFilter) setTypeFilter(saved.typeFilter);
    if (saved.urgencyFilter) setUrgencyFilter(saved.urgencyFilter);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urgencyParam = (params.get('urgency') || '').toUpperCase();
    if (['OVERDUE', 'DUE_SOON', 'UNALLOCATED', 'ALL'].includes(urgencyParam)) {
      setUrgencyFilter(urgencyParam || 'ALL');
    }
  }, [location.search]);

  // `?reappro=<station>&litres=<n>`: l'écran Répartition envoie ici pour commander le
  // réapprovisionnement d'une station — la commande s'ouvre déjà de cette nature, sur cette station.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const station = params.get('reappro');
    if (!station) return;
    const litres = Number(params.get('litres'));
    setFormData({
      ...createDefaultOrderForm(),
      type: 'INTERNAL_TRANSFER',
      supply_kind: NATURE_REAPPRO,
      destination_station_id: station,
      quantity_liters: Number.isFinite(litres) && litres > 0 ? String(Math.round(litres)) : '',
    });
    setShowNewModal(true);
    params.delete('reappro');
    params.delete('litres');
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  }, [location.search, location.pathname, navigate]);

  useEffect(() => {
    localStorage.setItem(
      STORAGE_PREFIX + 'ordersFilters',
      JSON.stringify({ searchQuery, statusFilter, typeFilter, urgencyFilter }),
    );
  }, [searchQuery, statusFilter, typeFilter, urgencyFilter]);

  const clientsById = useMemo(
    () => new Map(clients.map((c) => [c.id, c])),
    [clients],
  );

  const clientOptions = useMemo(
    () =>
      clients.map((client) => ({
        value: client.id,
        label: client.name || 'Client sans nom',
        description: client.city || undefined,
        keywords: [client.contact_name, client.phone, client.tax_id],
      })),
    [clients],
  );

  const selectedClient = useMemo(
    () => clientsById.get(formData.client_id),
    [clientsById, formData.client_id],
  );

  // Le prix de vente que la société a déjà déclaré pour ce client. La table des
  // barèmes, son écran de saisie et la route de résolution existaient; aucun
  // formulaire ne les appelait, et l'exploitant retapait le même prix à chaque
  // commande. On ne résout qu'à l'ouverture de la création: l'écran de liste
  // n'a rien à en faire.
  const {
    prix: prixBareme,
    nomBareme,
    chargement: chargementBareme,
    aucunBareme,
  } = usePrixPropose({
    clientId: formData.type === 'SALE' ? formData.client_id : null,
    clientType: selectedClient?.type,
    actif: showNewModal,
  });

  // Le prix négocié de la fiche client est désormais résolu **par le serveur**,
  // en tête de `PricingService`: un accord passé avec quelqu'un l'emporte sur
  // tout barème. Le garder ici en second recours ferait deux endroits qui
  // décident du même prix, et c'est précisément ce qu'on vient de défaire.
  //
  // Il reste un repli local, et un seul: hors ligne, la résolution ne répond
  // pas, et la fiche client est le seul prix dont l'écran dispose.
  const prixFicheClient =
    Number(selectedClient?.default_price_per_liter) > 0
      ? Number(selectedClient.default_price_per_liter)
      : null;
  const prixPropose = prixBareme ?? prixFicheClient;

  // Le lieu où ce client se fait livrer d'habitude. Saisi une fois sur sa
  // fiche, il n'a pas à être retapé à chaque commande.
  const lieuFicheClient = selectedClient?.address || selectedClient?.city || '';

  useEffect(() => {
    if (!showNewModal) return;
    // Tant que la résolution tourne, `prixBareme` porte encore le prix du
    // client précédent. L'appliquer ici facturerait le client B au tarif du
    // client A — et le champ, une fois rempli, ne serait plus corrigé.
    if (chargementBareme) return;
    setFormData((prev) => ({
      ...prev,
      unit_price: appliquerPrixPropose({
        prix: prixPropose,
        valeurActuelle: prev.unit_price,
        toucheParLHumain: prixTouche,
      }),
      delivery_location:
        lieuTouche || prev.delivery_location
          ? prev.delivery_location
          : lieuFicheClient,
    }));
  }, [
    showNewModal,
    formData.client_id,
    chargementBareme,
    prixPropose,
    prixTouche,
    lieuFicheClient,
    lieuTouche,
  ]);

  const estimatedTotal = useMemo(
    () => montantEstimeCommande(formData),
    [formData],
  );

  const creditLimit = Number(selectedClient?.credit_limit || 0);
  const currentBalance = Number(selectedClient?.current_balance || 0);
  const isCreditLimitExceeded =
    formData.type === 'SALE' &&
    creditLimit > 0 &&
    currentBalance + estimatedTotal > creditLimit;

  const openNewOrderModal = () => {
    setFormData(createDefaultOrderForm());
    setPrixTouche(false);
    setLieuTouche(false);
    setShowNewModal(true);
  };

  /**
   * Changer de client remet à zéro ce qui venait du précédent.
   *
   * Sans cela, le prix du client A resterait dans le champ pour le client B:
   * le barème ne remplit que les champs vides, donc une valeur héritée d'un
   * autre client survivrait à la sélection sans que rien ne le signale.
   */
  const choisirClient = (value) => {
    setFormData((prev) => ({
      ...prev,
      client_id: value,
      unit_price: prixTouche ? prev.unit_price : '',
      delivery_location: lieuTouche ? prev.delivery_location : '',
    }));
  };

  const handleOrderTypeChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      type: value,
      client_id: value === 'SALE' ? prev.client_id : '',
    }));
  };

  /**
   * Nature de la commande (lot 032). Le réapprovisionnement d'une station de
   * la société n'a ni client ni prix : type « transfert interne ».
   */
  const changerNature = (champs) => {
    setFormData((prev) => {
      const suite = { ...prev, ...champs };
      if (champs.supply_kind === NATURE_REAPPRO) {
        return { ...suite, type: 'INTERNAL_TRANSFER', client_id: '', unit_price: '', unit_cost: '' };
      }
      if (champs.supply_kind === NATURE_VENTE && prev.supply_kind === NATURE_REAPPRO) {
        return { ...suite, type: 'SALE' };
      }
      return suite;
    });
  };

  const getRemainingQuantity = useCallback(remainingToDeliver, []);

  const getOrderUrgency = useCallback((order) => {
    const status = String(order.status || 'DRAFT');
    const inactiveStatuses = ['DELIVERED', 'CANCELLED', 'PAID', 'INVOICED'];
    if (inactiveStatuses.includes(status)) return null;

    const remaining = getRemainingQuantity(order);
    if (remaining <= 0) return null;

    const requestedDateValue = order.requested_delivery_date;
    const requestedDate = requestedDateValue ? new Date(requestedDateValue) : null;
    const hasRequestedDate = requestedDate && !Number.isNaN(requestedDate.getTime());

    if (hasRequestedDate) {
      const dueDate = new Date(requestedDate);
      dueDate.setHours(0, 0, 0, 0);

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (dueDate < today) {
        return 'OVERDUE';
      }

      const diffDays = Math.round(
        (dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
      );
      if (diffDays <= 2) {
        return 'DUE_SOON';
      }
    }

    // « Pas de camion » se lit sur l'avancement calculé par le serveur: le
    // cache `allocated_trips` n'est plus l'autorité.
    if (progressOf(order).allocated_liters <= 0) {
      return 'UNALLOCATED';
    }

    return null;
  }, [getRemainingQuantity]);

  const urgentCounts = useMemo(() => {
    const counts = { OVERDUE: 0, DUE_SOON: 0, UNALLOCATED: 0 };
    for (const order of orders) {
      const urgency = getOrderUrgency(order);
      if (urgency) counts[urgency] += 1;
    }
    return counts;
  }, [orders, getOrderUrgency]);

  // Statut effectif: « Facturée » = une facture non annulée existe. Une
  // commande restée « Facturée » dont la facture a été annulée redevient livrée.
  const factureDe = (order) => facturesParCommande?.get(order.id) || null;
  const statutEffectif = (order) => {
    if (!facturesParCommande) return order.status;
    if (facturesParCommande.has(order.id)) return 'INVOICED';
    return ['INVOICED', 'PAID'].includes(order.status) ? 'DELIVERED' : order.status;
  };
  const nombreFacturees = orders.filter((order) => statutEffectif(order) === 'INVOICED').length;

  const filtered = orders.filter((order) => {
    const clientName = clientsById.get(order.client_id)?.name || '';
    const statusMatch = statusFilter === 'ALL' || statutEffectif(order) === statusFilter;
    const typeMatch = typeFilter === 'ALL' || order.type === typeFilter;
    const orderUrgency = getOrderUrgency(order);
    const urgencyMatch =
      urgencyFilter === 'ALL' ||
      (urgencyFilter === 'OVERDUE' && orderUrgency === 'OVERDUE') ||
      (urgencyFilter === 'DUE_SOON' && orderUrgency === 'DUE_SOON') ||
      (urgencyFilter === 'UNALLOCATED' && orderUrgency === 'UNALLOCATED');
    return (
      statusMatch &&
      typeMatch &&
      urgencyMatch &&
      (String(order.reference_number || '')
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
        clientName.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  });

  const pagination = usePagination(filtered, {
    reinitialiser: [searchQuery, statusFilter, typeFilter, urgencyFilter].join('|'),
  });

  const kpis = useMemo(() => {
    const saleOrders = orders.filter((o) => o.type === 'SALE');
    const totalCount = saleOrders.length || 1;
    const avgUnitPrice =
      saleOrders.reduce((sum, o) => sum + Number(o.unit_price || 0), 0) /
      totalCount;
    const avgUnitCost =
      saleOrders.reduce((sum, o) => sum + Number(o.unit_cost || 0), 0) /
      totalCount;
    const avgMargin = avgUnitPrice - avgUnitCost;

    const deliveryTimes = saleOrders
      .filter((o) => o.actual_delivery_date && o.order_date)
      .map(
        (o) =>
          new Date(o.actual_delivery_date).getTime() -
          new Date(o.order_date).getTime(),
      )
      .filter((ms) => ms > 0);
    const avgDeliveryMs =
      deliveryTimes.reduce((sum, ms) => sum + ms, 0) /
      (deliveryTimes.length || 1);
    const avgDeliveryDays = avgDeliveryMs / (1000 * 60 * 60 * 24);

    return {
      avgUnitPrice,
      avgUnitCost,
      avgMargin,
      avgDeliveryDays,
    };
  }, [orders]);

  const pilotage = useMemo(() => {
    const activeOrders = orders.filter((order) => !['DELIVERED', 'INVOICED', 'CANCELLED', 'PAID'].includes(order.status));
    const orderedVolume = orders.reduce((sum, order) => sum + Number(order.quantity_liters || 0), 0);
    const deliveredVolume = orders.reduce((sum, order) => sum + Number(order.delivered_quantity || 0), 0);
    const remainingVolume = orders.reduce((sum, order) => sum + getRemainingQuantity(order), 0);
    // Une commande annulée n'engage rien; une marge sans coût d'achat connu
    // n'est pas une marge (le serveur la laisse inconnue): elle est exclue
    // au lieu de compter tout le prix de vente comme bénéfice.
    const engagees = orders.filter((order) => order.status !== 'CANCELLED');
    const totalAmount = sommeMontants(engagees.map((order) => order.total_amount));
    const estimatedMargin = sommeMontants(
      engagees.map(margeCommande).filter((marge) => marge !== null),
    );
    const blocked = activeOrders.filter((order) => getOrderUrgency(order)).length;
    return {
      activeCount: activeOrders.length,
      orderedVolume,
      deliveredVolume,
      remainingVolume,
      totalAmount,
      estimatedMargin,
      blocked,
    };
  }, [getOrderUrgency, getRemainingQuantity, orders]);

  const exportCSVLocal = () => {
    const headers = [
      'Référence',
      'Client',
      'Type',
      'Statut',
      'Quantité (L)',
      'Prix de vente (FCFA/L)',
      'Coût (FCFA/L)',
      'Montant (FCFA)',
    ];
    const rows = filtered.map((order) => [
      referenceLisible(order.reference_number),
      clientsById.get(order.client_id)?.name || '',
      libelle('orderType', order.type),
      orderStatusLabel(order),
      order.quantity_liters || 0,
      order.unit_price || 0,
      order.unit_cost || 0,
      order.total_amount || 0,
    ]);
    const csv = [headers, ...rows]
      .map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','),
      )
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'commandes.csv';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const exportCSV = async () => {
    try {
      const params = {
        dataset: 'orders',
      };
      if (statusFilter !== 'ALL') params.status = statusFilter;
      if (typeFilter !== 'ALL') params.type = typeFilter;

      const response = await exportAPI.downloadCSV(params);
      downloadBlob(response, 'commandes.csv', 'text/csv;charset=utf-8');
    } catch (err) {
      exportCSVLocal();
    }
  };

  const exportPDF = () => {
    const html = `
      <html><head><title>Commandes</title></head><body>
      <h2>Commandes</h2>
      <table border="1" cellspacing="0" cellpadding="6">
      <thead><tr>${[
        'Référence',
        'Client',
        'Type',
        'Statut',
        'Quantité (L)',
        'Montant (FCFA)',
      ]
        .map((h) => `<th>${h}</th>`)
        .join('')}</tr></thead>
      <tbody>
      ${filtered
        .map(
          (o) => `
            <tr>
              <td>${referenceLisible(o.reference_number)}</td>
              <td>${clientsById.get(o.client_id)?.name || ''}</td>
              <td>${libelle('orderType', o.type)}</td>
              <td>${orderStatusLabel(o)}</td>
              <td>${o.quantity_liters || 0}</td>
              <td>${o.total_amount || 0}</td>
            </tr>`,
        )
        .join('')}
      </tbody></table></body></html>`;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.focus();
    win.print();
  };

  const handleCreate = async () => {
    const requiresClient = formData.type === 'SALE' && !estReappro(formData);
    if ((requiresClient && !formData.client_id) || !formData.quantity_liters)
      return;
    const quantity = Number(formData.quantity_liters || 0);
    const unitPrice = Number(formData.unit_price || 0);
    const unitCost = Number(formData.unit_cost || 0);
    const totalAmount = formData.type === 'SALE' ? quantity * unitPrice : 0;

    const payload = {
      client_id: requiresClient ? formData.client_id : undefined,
      type: formData.type,
      status: formData.status || 'DRAFT',
      quantity_liters: quantity,
      // Nul quand la saisie s'est faite en litres: l'écran dérive l'équivalent
      // à l'affichage plutôt que de stocker un second nombre.
      quantity_cisterns: formData.quantity_cisterns || undefined,
      unit_price: formData.type === 'SALE' ? unitPrice : undefined,
      unit_cost: formData.type === 'SALE' ? unitCost : undefined,
      total_amount: totalAmount,
      delivery_location: formData.delivery_location,
      requested_delivery_date: formData.requested_delivery_date || undefined,
      reference_number: formData.reference_number || undefined,
      ...champsNature(formData),
    };

    try {
      const created = await ordersAPI.create(payload);

      // Les camions retenus pendant la saisie, posés dans la foulée. L'échec de
      // la répartition ne doit pas faire croire que la commande n'existe pas:
      // elle est créée, elle est dans la liste, et l'exploitant peut lui
      // ajouter des camions plus tard depuis la même ligne.
      const lignes = Object.entries(selectionCamions)
        .map(([trip_id, quantity]) => ({ trip_id, quantity: Number(quantity) }))
        .filter((ligne) => ligne.quantity > 0);
      if (lignes.length > 0) {
        try {
          await ordersAPI.allocateBatch(created.id, lignes);
        } catch (allocErr) {
          console.error('Commande créée, répartition refusée:', allocErr);
        }
      }

      setOrders((prev) => [created, ...prev]);
      fermerCreation();
      loadOrders();
    } catch (err) {
      console.error('Failed to create order:', err);
      toast.error(messageErreur(err, 'Impossible de créer la commande. Vérifiez les champs, puis réessayez.'));
    }
  };

  const fermerCreation = () => {
    setShowNewModal(false);
    setFormData(createDefaultOrderForm());
    setSelectionCamions({});
    setCreationClient(false);
    setNouveauClient(createDefaultClientForm());
    setPrixTouche(false);
    setLieuTouche(false);
  };

  /**
   * Cocher un camion lui confie ce dont la commande a encore besoin, dans la
   * limite de ce qu'il lui reste. Un exploitant qui coche trois citernes pour
   * 120 000 L n'a pas à calculer lui-même la répartition — et il peut toujours
   * corriger ensuite depuis l'écran de répartition.
   */
  const basculerCamion = (camion) => {
    setSelectionCamions((prev) => {
      const suite = { ...prev };
      if (suite[camion.trip_id] != null) {
        delete suite[camion.trip_id];
        return suite;
      }
      const dejaCouvert = Object.values(suite).reduce(
        (total, volume) => total + Number(volume || 0),
        0,
      );
      const manquant = Math.max(
        0,
        Number(formData.quantity_liters || 0) - dejaCouvert,
      );
      const disponible = Number(camion.available_quantity || 0);
      suite[camion.trip_id] = manquant > 0 ? Math.min(manquant, disponible) : disponible;
      return suite;
    });
  };

  const creerClientEnLigne = async () => {
    const nom = nouveauClient.name.trim();
    const telephone = nouveauClient.phone.trim();
    if (!nom || !telephone) return;
    try {
      const cree = await clientsAPI.create({ name: nom, phone: telephone });
      setClients((prev) => [cree, ...prev]);
      choisirClient(cree.id);
      setCreationClient(false);
      setNouveauClient(createDefaultClientForm());
    } catch (err) {
      console.error('Failed to create client inline:', err);
      toast.error(messageErreur(err, 'Impossible de créer le client. Réessayez.'));
    }
  };

  const openEditModal = (order) => {
    setSelectedOrder(order);
    setFormData({
      client_id: order.client_id || '',
      type: order.type || 'SALE',
      status: order.status || 'DRAFT',
      quantity_liters: order.quantity_liters || '',
      quantity_cisterns: order.quantity_cisterns ?? null,
      unit_price: order.unit_price || '',
      unit_cost: order.unit_cost || '',
      delivery_location: order.delivery_location || '',
      requested_delivery_date: order.requested_delivery_date || '',
      reference_number: order.reference_number || '',
      supply_kind: order.supply_kind || NATURE_VENTE,
      destination_station_id: order.destination_station_id || '',
      destination_tanks: order.destination_tanks || [],
    });
    setShowEditModal(true);
  };

  const handleUpdate = async () => {
    if (!selectedOrder) return;
    const requiresClient = formData.type === 'SALE' && !estReappro(formData);
    if ((requiresClient && !formData.client_id) || !formData.quantity_liters)
      return;
    const quantity = Number(formData.quantity_liters || 0);
    const unitPrice = Number(formData.unit_price || 0);
    const unitCost = Number(formData.unit_cost || 0);
    const totalAmount = formData.type === 'SALE' ? quantity * unitPrice : 0;

    const payload = {
      client_id: requiresClient ? formData.client_id : undefined,
      type: formData.type,
      status: formData.status || 'DRAFT',
      quantity_liters: quantity,
      quantity_cisterns: formData.quantity_cisterns || undefined,
      unit_price: formData.type === 'SALE' ? unitPrice : undefined,
      unit_cost: formData.type === 'SALE' ? unitCost : undefined,
      total_amount: totalAmount,
      delivery_location: formData.delivery_location,
      requested_delivery_date: formData.requested_delivery_date || undefined,
      reference_number: formData.reference_number || undefined,
      ...champsNature(formData, selectedOrder.supply_kind),
    };

    try {
      const updated = await ordersAPI.update(selectedOrder.id, payload);
      setOrders((prev) =>
        prev.map((order) => (order.id === updated.id ? updated : order)),
      );
      setShowEditModal(false);
      setSelectedOrder(null);
    } catch (err) {
      console.error('Failed to update order:', err);
      toast.error(messageErreur(err, 'Impossible de modifier la commande. Réessayez.'));
    }
  };

  const confirmDelete = (order) => {
    setOrderToDelete(order);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!orderToDelete) return;
    try {
      await ordersAPI.delete(orderToDelete.id);
      setOrders((prev) =>
        prev.filter((order) => order.id !== orderToDelete.id),
      );
    } catch (err) {
      console.error('Failed to delete order:', err);
      toast.error(messageErreur(err, 'Impossible de supprimer la commande. Réessayez.'));
    } finally {
      setShowDeleteModal(false);
      setOrderToDelete(null);
    }
  };

  const handleAutoAllocate = async (order) => {
    try {
      await ordersAPI.autoAllocate(order.id);
      await loadOrders();
    } catch (err) {
      console.error('Failed to auto allocate order:', err);
      toast.error(messageErreur(err, 'Impossible d’affecter les camions automatiquement. Réessayez.'));
    }
  };

  const confirmerCommande = async (order) => {
    try {
      await ordersAPI.confirm(order.id);
      await loadOrders();
    } catch (err) {
      console.error('Failed to confirm order:', err);
      toast.error(messageErreur(err, 'Impossible de confirmer la commande. Réessayez.'));
    }
  };

  const annulerCommande = async (order) => {
    // Le motif est exigé par le serveur, et à raison: une commande annulée sans
    // explication est une question qui revient la semaine suivante.
    const motif = window.prompt('Pourquoi cette commande est-elle annulée ?');
    if (!motif) return;
    try {
      await ordersAPI.cancel(order.id, motif);
      await loadOrders();
    } catch (err) {
      console.error('Failed to cancel order:', err);
      toast.error(messageErreur(err, 'Impossible d’annuler la commande. Réessayez.'));
    }
  };

  const blocActions = (order) => (
                      <div className="flex items-center gap-2 whitespace-nowrap">
                        {/* Un réapprovisionnement se livre par la réception en station (lot 032). */}
                        {!['DELIVERED', 'INVOICED', 'PAID', 'CANCELLED'].includes(order.status) && !estReappro(order) && (
                          <Button size="sm" onClick={() => openDeliveryModal(order)}>Livrer</Button>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" aria-label={`Autres actions pour ${referenceLisible(order.reference_number)}`}>
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {order.status === 'DRAFT' && (
                              <DropdownMenuItem onSelect={() => confirmerCommande(order)}>Confirmer</DropdownMenuItem>
                            )}
                            {factureDe(order) ? (
                              <DropdownMenuItem onSelect={() => voirFacture(factureDe(order))}>Voir la facture</DropdownMenuItem>
                            ) : statutEffectif(order) === 'DELIVERED' && order.type === 'SALE' ? (
                              <DropdownMenuItem disabled={generatingInvoice === order.id || !enLigne} onSelect={() => generateInvoice(order)}>Générer la facture</DropdownMenuItem>
                            ) : null}
                            {!['DELIVERED','INVOICED','PAID','CANCELLED'].includes(order.status) && <DropdownMenuItem onSelect={() => openClose(order)}>Terminer la livraison</DropdownMenuItem>}
                            <DropdownMenuItem onSelect={() => openEditModal(order)}>Modifier</DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => openLogModal(order)}>Historique</DropdownMenuItem>
                            {progressOf(order).can_allocate && (
                              <DropdownMenuItem onSelect={() => handleAutoAllocate(order)}>Affecter automatiquement</DropdownMenuItem>
                            )}
                            {!['DELIVERED', 'INVOICED', 'PAID', 'CANCELLED'].includes(order.status) && (
                              <DropdownMenuItem onSelect={() => annulerCommande(order)}>Annuler</DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-destructive focus:text-destructive" onSelect={() => confirmDelete(order)}>Supprimer</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
  );

  const voirFacture = (invoice) => {
    navigate(`/invoices?invoice_id=${encodeURIComponent(invoice.id)}`);
  };

  const generateInvoice = async (order) => {
    setGeneratingInvoice(order.id);
    try {
      const invoice = await invoicesAPI.generateForOrder(order.id);
      toast.success('Facture générée');
      if (invoice.advisories?.length) toast.info([...new Set(invoice.advisories.map((item) => messageErreur(item.message, 'Vérifiez cette facture avant de l’envoyer.')))].join(' '));
      await loadOrders();
      navigate(`/invoices?invoice_id=${encodeURIComponent(invoice.id)}`);
    } catch (error) {
      toast.error(messageErreur(error, 'Impossible de générer la facture. Réessayez.'));
    } finally {
      setGeneratingInvoice(null);
    }
  };

  const openLogModal = (order) => {
    setSelectedOrder(order);
    setShowLogModal(true);
  };

  /**
   * La livraison se saisit **camion par camion**, pas en « segments ».
   *
   * L'ancienne modale demandait un volume, un lieu, une date et un trajet à
   * choisir dans toute la flotte — alors que le système sait déjà quels camions
   * portent cette commande et combien chacun doit déposer. Et c'est précisément
   * cette ligne-là qui décide de la suite: un camion qui a tout dépoté passe
   * « livré », un camion qui garde du volume reste disponible pour un autre
   * client.
   */
  const openDeliveryModal = async (order) => {
    setSelectedOrder(order);
    setShowDeliveryModal(true);
    setChargementLignes(true);
    const allocations = await ordersAPI.getAllocations(order.id);
    setChargementLignes(false);

    if (allocations.length > 0) {
      setLignesLivraison(
        allocations.map((ligne) => ({
          trip_id: ligne.trip_id,
          delivery_id: crypto.randomUUID(),
          delivery_date: new Date().toISOString(),
          matricule: ligne.truck_matricule,
          alloue: Number(ligne.quantity_liters || 0),
          deja: Number(ligne.delivered_quantity || 0),
          aDepoter: Number(ligne.reste_a_depoter || 0),
          // Préremplie avec ce qui reste à dépoter: c'est le cas courant, et
          // l'exploitant corrige quand le camion n'a pas tout déchargé.
          saisie: ligne.reste_a_depoter ? String(ligne.reste_a_depoter) : '',
        })),
      );
      return;
    }

    // Une commande peut être livrée sans qu'aucun camion ne lui ait été alloué
    // — une vente prise sur le stock, un dépannage. On garde une ligne libre.
    setLignesLivraison([
      {
        trip_id: '',
        delivery_id: crypto.randomUUID(),
        delivery_date: new Date().toISOString(),
        matricule: null,
        alloue: 0,
        deja: 0,
        aDepoter: getRemainingQuantity(order),
        saisie: '',
      },
    ]);
  };

  const majLigneLivraison = (index, saisie) =>
    setLignesLivraison((prev) =>
      prev.map((ligne, i) => (i === index ? { ...ligne, saisie } : ligne)),
    );

  const handleDelivery = async () => {
    if (!selectedOrder || savingDelivery) return;
    const lignes = lignesLivraison.filter(
      (ligne) => Number(ligne.saisie || 0) > 0,
    );
    if (lignes.length === 0) return;

    setSavingDelivery(true);
    try {
      // En série, jamais en parallèle: chaque enregistrement lit le volume déjà
      // livré de la commande puis y ajoute le sien. Envoyés ensemble, deux
      // camions lisaient le même total de départ et le second écrasait le
      // premier — la commande n'enregistrait qu'une des deux livraisons.
      for (const ligne of lignes) {
        await ordersAPI.recordDelivery(selectedOrder.id, {
          delivery_id: ligne.delivery_id,
          delivery_date: ligne.delivery_date,
          quantity_liters: Number(ligne.saisie || 0),
          delivery_location: selectedOrder.delivery_location || undefined,
          trip_id: ligne.trip_id || undefined,
        });
      }
      await loadOrders();
      setShowDeliveryModal(false);
      setSelectedOrder(null);
    } catch (err) {
      console.error('Failed to record delivery:', err);
      toast.error('Livraison non confirmée. Les saisies sont conservées pour réessayer.');
    } finally {
      setSavingDelivery(false);
    }
  };

  // `kpis` divise par `|| 1` pour ne jamais tomber sur une division par zéro:
  // sans aucune commande de vente, la moyenne vaut donc 0 FCFA, ce qui se lit
  // comme un vrai prix à zéro. Tant qu'aucune vente n'existe, la tuile affiche
  // « — »: une donnée absente n'est pas une donnée nulle.
  const aDesVentes = orders.some((order) => order.type === 'SALE');

  // Le reste à livrer n'est un problème que s'il est déjà en retard. Sans
  // commande en retard, un restant est simplement du travail à venir: il
  // garde le ton « atelier » plutôt que de crier au rouge.
  const tonReste =
    pilotage.remainingVolume > 0 && urgentCounts.OVERDUE > 0
      ? 'critique'
      : 'atelier';

  // Le bandeau du bas explique à quoi sert une commande. C'est utile au
  // premier jour, insultant au trois-centième: on le retire dès que l'écran
  // n'est visiblement plus celui d'un débutant.
  const afficherPedagogie = !isLoading && orders.length <= 3;

  return (
    <div className="mx-auto w-full max-w-[1800px] space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      <PageHeader
        eyebrow="Vendre le carburant"
        title="Commandes"
        actionsDisposition="grille"
        stats={[
          {
            label: 'Commandes actives',
            priorite: 2,
            valeur: pilotage.activeCount,
          },
          {
            label: 'Commandés',
            priorite: 3,
            valeur: formatCompactVolume(pilotage.orderedVolume),
            aide: `${formatCompactVolume(pilotage.deliveredVolume)} déjà livrés`,
          },
          {
            label: 'Restent à livrer',
            priorite: 1,
            valeur: formatCompactVolume(pilotage.remainingVolume),
            ton: tonReste === 'critique' ? 'critique' : 'neutre',
            aide:
              tonReste === 'critique'
                ? 'Au moins une commande est en retard'
                : 'Volume des commandes pas encore livré',
          },
          {
            label: 'Engagés',
            priorite: 4,
            valeur: formatCompactCurrency(pilotage.totalAmount),
            aide: `Marge estimée ${formatCompactCurrency(pilotage.estimatedMargin)}`,
          },
        ]}
        statsPlus={
          <dl className="space-y-1.5 text-sm">
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">Livrés</dt>
              <dd className="font-semibold tabular-nums">
                {formatCompactVolume(pilotage.deliveredVolume)}
              </dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">Marge estimée</dt>
              <dd className="font-semibold tabular-nums">
                {formatCompactCurrency(pilotage.estimatedMargin)}
              </dd>
            </div>
            {aDesVentes ? (
              <>
                <div className="flex justify-between gap-3">
                  <dt className="text-muted-foreground">Marge / L</dt>
                  <dd className="font-semibold tabular-nums">
                    {formatCurrency(kpis.avgMargin)}
                  </dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-muted-foreground">Prix moyen / L</dt>
                  <dd className="font-semibold tabular-nums">
                    {formatCurrency(kpis.avgUnitPrice)}
                  </dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-muted-foreground">Coût moyen / L</dt>
                  <dd className="font-semibold tabular-nums">
                    {formatCurrency(kpis.avgUnitCost)}
                  </dd>
                </div>
              </>
            ) : null}
          </dl>
        }
        actions={
          <>
            <Button variant="outline" onClick={exportCSV}>
              <Download className="mr-1.5 h-4 w-4" aria-hidden="true" />
              Exporter CSV
            </Button>
            <Button variant="outline" onClick={exportPDF}>
              <FileText className="mr-1.5 h-4 w-4" aria-hidden="true" />
              Exporter PDF
            </Button>
            <Button onClick={openNewOrderModal}>
              <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" />
              Nouvelle commande
            </Button>
          </>
        }
      />

      {/* Un camion parti livrer est déjà rendu disponible pour une nouvelle
          importation. Si personne ne solde sa commande, son trajet de livraison
          reste ouvert sans que rien ne le dise — la file le dit, ici, à
          l'endroit où l'on clôture les commandes. */}
      <BandeauFile
        icone={Truck}
        titre="À solder"
        nom="camions"
        nombre={aSolder.length}
        elements={aSolder}
        testid="orders-a-solder"
        motif={`${aSolder.length > 1 ? 'camions partis' : 'camion parti'} livrer, commande non soldée`}
        detail={
          aSolder.some((ligne) => ligne.overdue)
            ? `le plus ancien : ${Math.max(...aSolder.map((ligne) => Number(ligne.days) || 0))} j`
            : undefined
        }
        renderListe={(lignes, fermer) => (
          <ul className="divide-y divide-border/50">
            {lignes.map((ligne) => (
              <li
                key={ligne.trip_id}
                className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-2 py-2 text-sm"
              >
                <span className="font-semibold text-foreground">
                  {ligne.truck_matricule || 'Camion'}
                </span>
                <span
                  className={cn(
                    'tabular-nums',
                    ligne.overdue ? 'text-destructive' : 'text-muted-foreground',
                  )}
                >
                  {ligne.days} jour{ligne.days > 1 ? 's' : ''}
                </span>
                <span className="text-muted-foreground">
                  {formatVolume(ligne.pending_liters)} à remettre
                </span>
                <span className="basis-full text-xs text-muted-foreground sm:order-last sm:basis-full">
                  {(ligne.orders || [])
                    .map((c) => c.reference_number)
                    .filter(Boolean)
                    .join(', ')}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  className="ml-auto"
                  onClick={() => {
                    const cible = orders.find((o) =>
                      (ligne.orders || []).some((c) => c.order_id === o.id),
                    );
                    if (cible) {
                      fermer();
                      openDeliveryModal(cible);
                    }
                  }}
                >
                  Enregistrer la livraison
                </Button>
              </li>
            ))}
          </ul>
        )}
      />

      <SectionCard
        icon={Search}
        title="Liste des commandes"
        // La maquette annonçait « par client, produit ou référence ». La
        // commande n'a pas de colonne « produit » côté API: le sous-titre dit
        // donc ce que la barre sait réellement faire, sinon l'exploitant tape
        // « gasoil » et conclut que la recherche est cassée.
        subtitle="Recherchez une commande par client, statut ou référence."
        // La recherche et les trois filtres montent dans l'en-tête de la carte,
        // à droite du titre: une rangée de moins avant la première commande.
        outils={
          <div className="grid w-full grid-cols-3 gap-2 lg:flex lg:flex-wrap lg:items-center lg:justify-end">
            {/* Le champ ne cherche que dans la référence et le nom du client:
                la commande n'a pas de colonne « produit » côté API, donc le
                texte d'aide ne promet pas ce que la recherche ne sait pas
                faire. */}
            <Input
              className="col-span-3 w-full lg:w-56"
              aria-label="Rechercher une commande"
              placeholder="Rechercher un client, une référence…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full min-w-0 px-2 text-xs sm:text-sm lg:w-40 lg:px-3">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Statut : tous</SelectItem>
                {['DRAFT', 'CONFIRMED', 'ALLOCATED', 'IN_TRANSIT', 'PARTIALLY_DELIVERED', 'DELIVERED', 'INVOICED', 'PAID', 'CANCELLED'].map((code) => (
                  <SelectItem key={code} value={code}>
                    {libelle('statut_commande', code)}
                    {code === 'INVOICED' ? ` (${nombreFacturees})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full min-w-0 px-2 text-xs sm:text-sm lg:w-40 lg:px-3">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Type : tous</SelectItem>
                <SelectItem value="SALE">{libelle('orderType', 'SALE')}</SelectItem>
                <SelectItem value="INTERNAL_TRANSFER">{libelle('orderType', 'INTERNAL_TRANSFER')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={urgencyFilter} onValueChange={setUrgencyFilter}>
              <SelectTrigger className="w-full min-w-0 px-2 text-xs sm:text-sm lg:w-40 lg:px-3">
                <SelectValue placeholder="Urgence" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Urgence : toutes</SelectItem>
                <SelectItem value="OVERDUE">
                  En retard ({urgentCounts.OVERDUE})
                </SelectItem>
                <SelectItem value="DUE_SOON">
                  Échéance proche ({urgentCounts.DUE_SOON})
                </SelectItem>
                <SelectItem value="UNALLOCATED">
                  À affecter ({urgentCounts.UNALLOCATED})
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        }
        contentClassName="pt-3"
      >
        <div>
          {isLoading ? (
            <LoadingState shape="table" />
          ) : filtered.length === 0 ? (
            <EmptyState
              variant={orders.length === 0 ? 'empty' : 'filtered'}
              // L'illustration n'est servie qu'à l'écran réellement vide: une
              // liste filtrée à vide s'affiche au milieu d'un écran déjà
              // occupé, l'icône y suffit.
              illustration={orders.length === 0 ? 'donnee' : undefined}
              icon={ShoppingCart}
              title={orders.length === 0 ? 'Aucune commande enregistrée' : 'Aucun résultat'}
              description={
                orders.length === 0
                  ? 'Une commande dit qui veut quel produit, quel volume et pour quand. C’est elle qui alimente la répartition, les trajets et la facture.'
                  : 'Aucune commande ne correspond aux filtres actifs.'
              }
              action={
                orders.length === 0 ? (
                  <div className="flex flex-col items-center gap-3">
                    <Button size="sm" onClick={openNewOrderModal}>
                      <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" />
                      Créer une nouvelle commande
                    </Button>
                    {/* Une commande de vente se rattache à un client: sans
                        aucun client, le formulaire est une impasse et le
                        détour par le portefeuille est le vrai premier pas.
                        Dès qu'un client existe, ce lien n'est plus qu'un
                        détour inutile — il disparaît. */}
                    {clients.length === 0 ? (
                      <button
                        type="button"
                        onClick={() => navigate('/clients')}
                        className="rounded-md text-xs font-medium text-muted-foreground underline underline-offset-4 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        Ou créer d’abord un client →
                      </button>
                    ) : null}
                  </div>
                ) : undefined
              }
            />
          ) : etroit ? (
            <ul className="space-y-3">
              {pagination.lignes.map((order) => {
                const orderUrgency = getOrderUrgency(order);
                const facture = factureDe(order);
                return (
                  <li key={order.id} className="min-w-0 rounded-xl border border-border bg-card p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-mono text-xs font-semibold text-foreground">{referenceLisible(order.reference_number)}</p>
                        <p className="break-words font-medium text-foreground">{clientsById.get(order.client_id)?.name || order.destination_station_name || 'Aucun client'}</p>
                        <p className="break-words text-xs text-muted-foreground">
                          {order.delivery_location || 'Lieu non renseigné'}
                          {order.requested_delivery_date ? ` · ${formatDate(order.requested_delivery_date)}` : ''}
                        </p>
                      </div>
                      {blocActions(order)}
                    </div>
                    <p className="mt-2 text-xs tabular-nums text-muted-foreground">
                      commandé {formatVolume(order.quantity_liters || order.quantity || 0)} · affecté {formatVolume(progressOf(order).allocated_liters || 0)} · livré {formatVolume(order.delivered_quantity || 0)} · reste {formatVolume(getRemainingQuantity(order))}
                    </p>
                    <p className="mt-1 text-sm font-semibold text-foreground">{formatCurrency(order.total_amount || 0)}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <Badge className="status-badge bg-muted text-muted-foreground">
                        {statutEffectif(order) === 'INVOICED'
                          ? libelle('statut_commande', 'INVOICED')
                          : statutEffectif(order) !== order.status
                            ? libelle('statut_commande', statutEffectif(order))
                            : orderStatusLabel(order)}
                      </Badge>
                      {orderUrgency ? (
                        <Badge className={cn('status-badge', orderUrgency === 'OVERDUE' ? 'bg-destructive/15 text-destructive' : orderUrgency === 'DUE_SOON' ? 'bg-flame/15 text-flame-ink' : 'bg-info/15 text-info')}>
                          {orderUrgency === 'OVERDUE' ? 'En retard' : orderUrgency === 'DUE_SOON' ? 'Échéance proche' : 'À affecter'}
                        </Badge>
                      ) : null}
                    </div>
                    {facture ? (
                      <p className="mt-2 break-words text-xs text-muted-foreground">
                        {String(facture.invoice_number || '').toUpperCase() === 'DRAFT' ? 'Sans numéro' : referenceLisible(facture.invoice_number, 'Sans numéro')} · {libelle('invoiceStatus', facture.status, 'Brouillon')} ·{' '}
                        <button type="button" onClick={() => voirFacture(facture)} className="rounded-sm font-medium text-primary underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                          Voir la facture
                        </button>
                      </p>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : (
            // Sept colonnes: sur un portable, le tableau défile
            // horizontalement plutôt que d'écraser les montants.
            <div className="-mx-1 overflow-x-auto px-1">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Commande</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Volumes</TableHead>
                  <TableHead>Montant / marge</TableHead>
                  <TableHead>Prochaine action</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagination.lignes.map((order) => {
                  const orderUrgency = getOrderUrgency(order);

                  return (
                  <TableRow key={order.id}>
                    <TableCell>
                      <p className="whitespace-nowrap font-mono text-xs font-semibold text-foreground">
                        {referenceLisible(order.reference_number)}
                      </p>
                      {/* `accent-fuel` n'existe dans aucun thème: la classe ne
                          produisait aucune couleur et la puce « Vente client »
                          restait transparente. On reprend la teinte neutre de
                          marque — vente ou transfert est une catégorie, pas un
                          état, donc pas de vert ni de rouge ici. */}
                      <Badge
                        className={cn(
                          'status-badge mt-1',
                          order.type === 'INTERNAL_TRANSFER'
                            ? 'bg-muted text-muted-foreground'
                            : 'bg-primary/10 text-primary',
                        )}
                      >
                        {libelle('orderType', order.type === 'INTERNAL_TRANSFER' ? 'INTERNAL_TRANSFER' : 'SALE')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <p className="font-medium text-foreground">{clientsById.get(order.client_id)?.name || order.destination_station_name || 'Aucun client'}</p>
                      <p className="text-xs text-muted-foreground">{order.delivery_location || 'Lieu non renseigné'}</p>
                    </TableCell>
                    <TableCell>
                      <Badge className="status-badge bg-muted text-muted-foreground">
                        {statutEffectif(order) === 'INVOICED'
                          ? libelle('statut_commande', 'INVOICED')
                          : statutEffectif(order) !== order.status
                            ? libelle('statut_commande', statutEffectif(order))
                            : orderStatusLabel(order)}
                      </Badge>
                      {factureDe(order) ? (
                        <p className="mt-1 text-xs text-muted-foreground">
                          Facture {String(factureDe(order).invoice_number || '').toUpperCase() === 'DRAFT' ? 'sans numéro' : referenceLisible(factureDe(order).invoice_number, 'sans numéro')} ·{' '}
                          {libelle('invoiceStatus', factureDe(order).status, 'Brouillon')}
                          {' · '}
                          <button
                            type="button"
                            onClick={() => voirFacture(factureDe(order))}
                            className="rounded-sm font-medium text-primary underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            Voir la facture
                          </button>
                        </p>
                      ) : null}
                      {orderUrgency ? (
                        <Badge
                          className={cn(
                            'status-badge ml-2',
                            orderUrgency === 'OVERDUE'
                              ? 'bg-destructive/15 text-destructive'
                              : orderUrgency === 'DUE_SOON'
                                ? 'bg-flame/15 text-flame-ink'
                                : 'bg-info/15 text-info',
                          )}
                        >
                          {orderUrgency === 'OVERDUE'
                            ? 'En retard'
                            : orderUrgency === 'DUE_SOON'
                              ? 'Échéance proche'
                              : 'À affecter'}
                        </Badge>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <p className="text-sm font-semibold text-foreground">
                        {formatVolume(order.quantity_liters || order.quantity || 0)}
                      </p>
                      {/* Le compte en citernes n'est affiché que sur les
                          commandes comptées ainsi: le dériver partout ferait
                          passer une unité de saisie pour une unité métier. */}
                      {order.quantity_cisterns ? (
                        <p className="text-xs font-medium text-foreground/70">
                          {formatVolumeWithUnit(
                            order.quantity_liters || 0,
                            { cistern_liters: order.cistern_liters_snapshot },
                            UNIT_CISTERNS,
                          )}
                        </p>
                      ) : null}
                      <p className="text-xs text-muted-foreground">
                        livré {formatVolume(order.delivered_quantity || 0)} · reste {formatVolume(getRemainingQuantity(order))}
                      </p>
                    </TableCell>
                    <TableCell>
                      <p className="font-semibold text-foreground">{formatCurrency(order.total_amount || 0)}</p>
                      <p className="text-xs text-muted-foreground">
                        marge {margeCommande(order) === null ? 'inconnue' : formatCurrency(margeCommande(order))}
                      </p>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm font-medium text-foreground">
                        {nextOrderAction(order, facturesParCommande ? factureDe(order) : undefined)}
                      </span>
                      <p className="text-xs text-muted-foreground">
                        {orderUrgency ? 'Attention requise' : 'Rien à signaler'}
                      </p>
                    </TableCell>
                    <TableCell>
                      {blocActions(order)}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </div>
          )}
          {!isLoading && filtered.length > 0 ? (
            <BarrePagination pagination={pagination} nom="commandes" />
          ) : null}
        </div>
      </SectionCard>

      {/* Il restait ici un encadré teinté « Bon à savoir », avec sa pastille et
          son ampoule, pour affirmer qu'une commande bien renseignée permet une
          meilleure planification. C'est vrai et ça n'apprend rien: personne
          n'ouvre cet écran en se demandant s'il vaut mieux mal renseigner.
          Le seul élément qui valait d'être gardé est le chemin vers le
          parcours de démarrage — le seul endroit qui explique réellement la
          chaîne commande → répartition → facture — et il ne se montre, comme
          avant, que tant que l'écran est presque vide. */}
      {afficherPedagogie ? (
        <Button
          variant="outline"
          size="sm"
          className="h-auto min-h-9 w-fit max-w-full whitespace-normal py-1.5 text-left"
          onClick={() => navigate(ONBOARDING_ROUTE)}
        >
          Découvrir la chaîne commande → répartition → facture
          <ExternalLink className="ml-1.5 h-4 w-4" aria-hidden="true" />
        </Button>
      ) : null}

      {/* Un client créé depuis l'écran Clients doit apparaître ici sans
          recharger la page: la liste est relue à chaque ouverture. */}
      <Dialog
        open={showNewModal}
        onOpenChange={(open) => {
          if (open) {
            setShowNewModal(true);
            loadClients();
          } else {
            fermerCreation();
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden p-0">
          <DialogHeader>
            <DialogTitle className="px-6 pt-6">Nouvelle commande</DialogTitle>
          </DialogHeader>
          {/* Les trois cartouches gris qui coiffaient chaque bloc — « Client /
              Choisissez le bénéficiaire… », « Volume & prix / Le montant est
              recalculé… », « Livraison / Ces informations alimentent… » — ont
              disparu. Ils paraphrasaient les champs juste en dessous, et c'est
              exactement le remplissage d'écran qui fatigue un exploitant. */}
          <div className="max-h-[calc(90vh-132px)] space-y-4 overflow-y-auto px-6 py-3">
            <div className="space-y-1">
              <Label>Type</Label>
              <Select
                value={formData.type}
                onValueChange={handleOrderTypeChange}
                disabled={estReappro(formData)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Type de commande" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SALE">{libelle('orderType', 'SALE')}</SelectItem>
                  <SelectItem value="INTERNAL_TRANSFER">{libelle('orderType', 'INTERNAL_TRANSFER')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formData.type === 'SALE' ? (
              <div className="space-y-1">
                <Label>Client</Label>
                {/* Une liste vide sans explication laisse croire qu'aucun
                    client n'existe: on distingue le parc vide de l'échec. */}
                <SearchableSelect
                  value={formData.client_id || ''}
                  onValueChange={choisirClient}
                  options={clientOptions}
                  placeholder="Sélectionner un client"
                  searchPlaceholder="Rechercher un client…"
                  emptyMessage={
                    clients.length === 0
                      ? clientsError || 'Aucun client enregistré.'
                      : 'Aucun client trouvé'
                  }
                />
                <div className="flex items-center gap-3">
                  {clientsError ? (
                    <button
                      type="button"
                      onClick={loadClients}
                      className="text-xs font-medium text-primary hover:underline"
                    >
                      Réessayer le chargement des clients
                    </button>
                  ) : null}
                  {/* Sans cela, un client nouveau oblige à fermer la commande,
                      aller dans Clients, revenir — et retrouver une saisie
                      perdue. C'est le geste du premier jour. */}
                  <button
                    type="button"
                    onClick={() => setCreationClient((ouvert) => !ouvert)}
                    className="text-xs font-medium text-primary hover:underline"
                  >
                    {creationClient ? 'Annuler' : '+ Nouveau client'}
                  </button>
                </div>
                {creationClient ? (
                  <div className="mt-2 space-y-2 rounded-xl border border-border/60 p-3">
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <Input
                        placeholder="Nom du client"
                        value={nouveauClient.name}
                        onChange={(e) =>
                          setNouveauClient((prev) => ({ ...prev, name: e.target.value }))
                        }
                      />
                      <Input
                        placeholder="Téléphone"
                        value={nouveauClient.phone}
                        onChange={(e) =>
                          setNouveauClient((prev) => ({ ...prev, phone: e.target.value }))
                        }
                      />
                    </div>
                    <Button
                      size="sm"
                      onClick={creerClientEnLigne}
                      disabled={!nouveauClient.name.trim() || !nouveauClient.phone.trim()}
                    >
                      Créer et sélectionner
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : null}
            <OrderSupplyFields
              valeur={formData}
              clientId={formData.client_id}
              actif={showNewModal}
              onChange={changerNature}
            />
            {isCreditLimitExceeded ? (
              <div className="rounded-lg border border-flame/30 bg-flame/10 px-3 py-2 text-xs text-flame-ink">
                Plafond indicatif dépassé :{' '}
                {formatCurrency(currentBalance + estimatedTotal)} /{' '}
                {formatCurrency(creditLimit)}. La commande reste autorisée.
              </div>
            ) : null}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <CisternInput
                label="Quantité"
                liters={formData.quantity_liters}
                cisterns={formData.quantity_cisterns}
                settings={volumeSettings}
                onChange={({ liters, cisterns }) =>
                  setFormData((prev) => ({
                    ...prev,
                    quantity_liters: liters,
                    quantity_cisterns: cisterns,
                  }))
                }
              />
              <div className="space-y-1">
                <Label>Prix de vente par litre</Label>
                <Input
                  value={formData.unit_price}
                  disabled={formData.type !== 'SALE'}
                  placeholder={
                    prixPropose != null ? String(prixPropose) : undefined
                  }
                  onChange={(e) => {
                    setPrixTouche(true);
                    setFormData((prev) => ({
                      ...prev,
                      unit_price: e.target.value,
                    }));
                  }}
                />
                {/* D'où vient le prix, ou pourquoi il n'y en a pas. Un champ
                    vide sans explication se remplit au jugé, et c'est ainsi
                    qu'un même client finit facturé à trois prix. */}
                {formData.type === 'SALE' && formData.client_id ? (
                  <p className="text-xs text-muted-foreground">
                    {chargementBareme
                      ? 'Lecture du barème…'
                      : nomBareme
                        ? `Barème « ${nomBareme} » — modifiable.`
                        : prixFicheClient != null
                          ? 'Prix par défaut de la fiche client — modifiable.'
                          : aucunBareme
                            ? 'Aucun barème ne couvre ce client : saisissez le prix convenu.'
                            : 'Modifiable : un prix négocié l’emporte sur le barème.'}
                  </p>
                ) : null}
              </div>
            </div>
            {/* Repliés: le coût d'achat ne vient d'aucun barème client — c'est
                ce que la société paie, pas ce qu'elle vend — et le lieu comme
                la date se déduisent de la fiche client et du jour même. Les
                trois restent saisissables, et le dépliant s'ouvre de lui-même
                dès que l'un d'eux porte une valeur. */}
            <ChampsAvances
              titre="Coût, lieu et date"
              consequence={
                lieuFicheClient && formData.delivery_location === lieuFicheClient
                  ? `Livraison à ${lieuFicheClient}, aujourd’hui, coût d’achat non renseigné.`
                  : 'Coût d’achat au litre, lieu de livraison et date souhaitée.'
              }
              valeurs={[formData.unit_cost]}
              nombre={3}
            >
              <div className="space-y-1">
                <div className="flex items-center gap-1">
                  <Label>Coût d’achat par litre</Label>
                  <Aide titre="Coût d’achat par litre">
                    Ce que la société paie le litre. Il ne vient pas du barème
                    client, qui est un prix de vente.
                  </Aide>
                </div>
                <Input
                  value={formData.unit_cost}
                  disabled={formData.type !== 'SALE'}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      unit_cost: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Lieu de livraison</Label>
                <Input
                  value={formData.delivery_location}
                  onChange={(e) => {
                    setLieuTouche(true);
                    setFormData((prev) => ({
                      ...prev,
                      delivery_location: e.target.value,
                    }));
                  }}
                />
              </div>
              <div className="space-y-1">
                <Label>Date souhaitée</Label>
                <Input
                  type="date"
                  value={formData.requested_delivery_date}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      requested_delivery_date: e.target.value,
                    }))
                  }
                />
              </div>
            </ChampsAvances>
            {/* Le geste qui manquait. Relier une commande à ses camions
                demandait jusqu'ici de créer un convoi, d'avancer chaque camion
                jusqu'au chargement, de saisir son volume mesuré, puis d'ouvrir
                un écran de répartition: le seizième geste d'un parcours de six
                écrans. Il est ici le troisième. */}
            <div className="space-y-2 border-t border-border/60 pt-4">
              <Label>Sur quels camions ?</Label>
              <CamionsAAllouer
                camions={camionsDisponibles}
                selection={selectionCamions}
                onToggle={basculerCamion}
                cible={Number(formData.quantity_liters || 0)}
                chargement={chargementCamions}
              />
            </div>

            <div className="rounded-lg border border-flame/30 bg-flame/10 p-3">
              <p className="text-xs font-semibold uppercase text-flame-ink">Résumé</p>
              <p className="mt-1 text-sm text-foreground">
                {formatVolume(formData.quantity_liters || 0)} ({formatVolumeWithUnit(formData.quantity_liters || 0, volumeSettings, UNIT_CISTERNS)}) · montant estimé {formatCurrency(estimatedTotal)} · marge estimée {formatCurrency((Number(formData.unit_price || 0) - Number(formData.unit_cost || 0)) * Number(formData.quantity_liters || 0))}
              </p>
              {/* Le lieu est replié: il doit donc se relire ici, sinon une
                  livraison déduite de la fiche client part sans que personne
                  ne l'ait vue. */}
              <p className="mt-0.5 text-xs text-muted-foreground">
                {formData.delivery_location
                  ? `Livraison à ${formData.delivery_location}`
                  : 'Lieu de livraison non renseigné'}
              </p>
            </div>
          </div>
          <DialogFooter className="border-t bg-card px-6 py-4">
            <Button variant="outline" onClick={fermerCreation}>
              Annuler
            </Button>
            <Button
              onClick={handleCreate}
              disabled={formData.type === 'SALE' && !formData.client_id}
            >
              {Object.keys(selectionCamions).length > 0
                ? `Créer et répartir sur ${Object.keys(selectionCamions).length} camion${Object.keys(selectionCamions).length > 1 ? 's' : ''}`
                : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden p-0">
          <DialogHeader>
            <DialogTitle className="px-6 pt-6">Modifier la commande</DialogTitle>
          </DialogHeader>
          <div className="max-h-[calc(90vh-132px)] space-y-4 overflow-y-auto px-6 py-3">
            {/* Même sort que son jumeau « Volume & prix »: « Vérifiez le
                client, le type de flux et le statut avant modification »
                recommandait de relire les trois champs qui suivent. */}
            <div className="space-y-1">
              <Label>Type</Label>
              <Select
                value={formData.type}
                onValueChange={handleOrderTypeChange}
                disabled={estReappro(formData)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Type de commande" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SALE">{libelle('orderType', 'SALE')}</SelectItem>
                  <SelectItem value="INTERNAL_TRANSFER">{libelle('orderType', 'INTERNAL_TRANSFER')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formData.type === 'SALE' ? (
              <div className="space-y-1">
                <Label>Client</Label>
                {/* Une liste vide sans explication laisse croire qu'aucun
                    client n'existe: on distingue le parc vide de l'échec. */}
                <SearchableSelect
                  value={formData.client_id || ''}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, client_id: value }))
                  }
                  options={clientOptions}
                  placeholder="Sélectionner un client"
                  searchPlaceholder="Rechercher un client…"
                  emptyMessage={
                    clients.length === 0
                      ? clientsError || 'Aucun client enregistré.'
                      : 'Aucun client trouvé'
                  }
                />
                {clientsError ? (
                  <button
                    type="button"
                    onClick={loadClients}
                    className="text-xs font-medium text-primary hover:underline"
                  >
                    Réessayer le chargement des clients
                  </button>
                ) : null}
              </div>
            ) : null}
            <OrderSupplyFields
              valeur={formData}
              clientId={formData.client_id}
              actif={showEditModal}
              onChange={changerNature}
              verrouille={(selectedOrder?.delivery_items || []).length > 0}
            />
            <div className="space-y-1">
              <Label>Statut</Label>
              <Select
                value={formData.status}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, status: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  {['DRAFT', 'CONFIRMED', 'ALLOCATED', 'IN_TRANSIT', 'PARTIALLY_DELIVERED', 'DELIVERED', 'CANCELLED'].map((code) => (
                    <SelectItem key={code} value={code}>
                      {libelle('statut_commande', code)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {isCreditLimitExceeded ? (
              <div className="rounded-lg border border-flame/30 bg-flame/10 px-3 py-2 text-xs text-flame-ink">
                Plafond indicatif dépassé :{' '}
                {formatCurrency(currentBalance + estimatedTotal)} /{' '}
                {formatCurrency(creditLimit)}. La commande reste autorisée.
              </div>
            ) : null}
            {/* Le dernier des cartouches gris « Volume & prix / Toute
                modification change le montant attendu… ». Ses trois frères ont
                sauté de la modale de création à la refonte précédente; celui-ci
                avait survécu dans la modale de modification, avec le mot
                « dispatch » en prime. Les deux champs qui suivent portent leur
                propre intitulé, et le montant recalculé s'affiche juste en
                dessous: la phrase annonçait ce qu'on voit se produire. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <CisternInput
                label="Quantité"
                liters={formData.quantity_liters}
                cisterns={formData.quantity_cisterns}
                settings={volumeSettings}
                onChange={({ liters, cisterns }) =>
                  setFormData((prev) => ({
                    ...prev,
                    quantity_liters: liters,
                    quantity_cisterns: cisterns,
                  }))
                }
              />
              <div className="space-y-1">
                <Label>Prix de vente par litre</Label>
                <Input
                  value={formData.unit_price}
                  disabled={formData.type !== 'SALE'}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      unit_price: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Lieu de livraison</Label>
              <Input
                value={formData.delivery_location}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    delivery_location: e.target.value,
                  }))
                }
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Date souhaitée</Label>
                <Input
                  type="date"
                  value={formData.requested_delivery_date}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      requested_delivery_date: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Référence</Label>
                <Input
                  value={formData.reference_number}
                  readOnly
                />
              </div>
            </div>
            <div className="rounded-lg border border-flame/30 bg-flame/10 p-3">
              <p className="text-xs font-semibold uppercase text-flame-ink">Résumé</p>
              <p className="mt-1 text-sm text-foreground">
                {formatVolume(formData.quantity_liters || 0)} · montant estimé {formatCurrency(estimatedTotal)} · marge estimée {formatCurrency((Number(formData.unit_price || 0) - Number(formData.unit_cost || 0)) * Number(formData.quantity_liters || 0))}
              </p>
            </div>
          </div>
          <DialogFooter className="border-t bg-card px-6 py-4">
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleUpdate}>
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Supprimer la commande</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Confirmer la suppression de la commande{' '}
            <span className="font-semibold">
              {referenceLisible(orderToDelete?.reference_number)}
            </span>
            ?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
              Annuler
            </Button>
            <Button
              className="bg-destructive hover:bg-destructive"
              onClick={handleDelete}
            >
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!closingOrder} onOpenChange={open => !open && !closeBusy && setClosingOrder(null)}><DialogContent className="max-w-lg"><DialogHeader><div className="flex items-center gap-1"><DialogTitle>Terminer la livraison</DialogTitle><Aide titre="Terminer la livraison">Les litres réellement reçus sont conservés. Un manquant reste un constat à suivre, sans bloquer la facturation.</Aide></div><DialogDescription className="sr-only">Les litres réellement reçus sont conservés. Un manquant reste un constat à suivre, sans bloquer la facturation.</DialogDescription></DialogHeader>
        <p>Livré : {Number(closingOrder?.delivered_quantity || 0).toLocaleString('fr-FR')} L · commandé : {Number(closingOrder?.quantity_liters || 0).toLocaleString('fr-FR')} L</p>
        <Label>Base contractuelle<select className="block border rounded-md bg-background p-2 w-full" value={closeForm.billing_basis} onChange={event=>editClose('billing_basis',event.target.value)}><option value="DELIVERED">Litres livrés (cas courant)</option><option value="LOADED">Litres chargés · selon contrat</option><option value="FLAT">Forfait · selon contrat</option></select></Label>
        {closeForm.billing_basis==='LOADED' && <Label>Volume chargé (L)<Input type="number" min="1" value={closeForm.loaded_liters} onChange={event=>editClose('loaded_liters',event.target.value)}/></Label>}
        {closeForm.billing_basis==='FLAT' && <Label>Montant forfaitaire (FCFA)<Input type="number" min="1" value={closeForm.flat_amount} onChange={event=>editClose('flat_amount',event.target.value)}/></Label>}
        <p className="font-semibold">Base avant taxes : {formatCurrency(closeForm.billing_basis==='FLAT'?Number(closeForm.flat_amount || 0):Number(closeForm.billing_basis==='LOADED'?closeForm.loaded_liters:closingOrder?.delivered_quantity || 0)*Number(closingOrder?.unit_price || 0))}</p>
        <Label>Motif de clôture<Input value={closeForm.reason || ''} onChange={event=>editClose('reason',event.target.value)}/></Label>{closeError && <p role="alert" className="text-destructive">{closeError}</p>}
        <DialogFooter><Button variant="outline" disabled={closeBusy} onClick={()=>setClosingOrder(null)}>Retour</Button><Button disabled={closeBusy || !closeForm.reason?.trim()} onClick={confirmClose}>Confirmer la clôture</Button></DialogFooter>
      </DialogContent></Dialog>
      <Dialog open={showLogModal} onOpenChange={setShowLogModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Historique de commande</DialogTitle>
            <DialogDescription>Étapes et décisions enregistrées pour cette commande.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {(selectedOrder?.activity_log || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Aucun historique disponible.
              </p>
            ) : (
              <div className="space-y-2">
                {selectedOrder.activity_log.map((event) => (
                  <div
                    key={event.id}
                    className="rounded-xl border border-border bg-card px-3 py-2"
                  >
                    <div className="text-xs text-muted-foreground">
                      {formatDateHeure(event.created_at)}
                    </div>
                    <div className="text-sm font-semibold text-foreground">
                      {libelle('orderEvent', event.event_type)}
                    </div>
                    {event.message ? (
                      <div className="text-xs text-muted-foreground">
                        {texteSur(event.message, '')}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLogModal(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeliveryModal} onOpenChange={setShowDeliveryModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden p-0">
          <DialogHeader>
            <DialogTitle className="px-6 pt-6">Enregistrer une livraison</DialogTitle>
          </DialogHeader>
          <div className="max-h-[calc(90vh-132px)] space-y-3 overflow-y-auto px-6 py-3">
            <div className="rounded-lg bg-muted/50 p-3">
              <p className="text-xs font-semibold uppercase text-muted-foreground">Commande sélectionnée</p>
              <p className="text-sm text-foreground">
                {referenceLisible(selectedOrder?.reference_number)} · reste {formatVolume(selectedOrder ? getRemainingQuantity(selectedOrder) : 0)}
              </p>
            </div>
            {chargementLignes ? (
              <p className="text-sm text-muted-foreground">
                Lecture des camions de la commande…
              </p>
            ) : null}
            {lignesLivraison.map((ligne, index) => (
              <div
                key={ligne.trip_id || `libre-${index}`}
                className="rounded-xl border border-border/60 p-3"
              >
                <div className="mb-2 flex items-baseline justify-between gap-3">
                  <p className="truncate text-sm font-semibold text-foreground">
                    {ligne.matricule || 'Sans camion'}
                  </p>
                  {ligne.trip_id ? (
                    <p className="shrink-0 text-xs text-muted-foreground">
                      alloué {formatVolume(ligne.alloue)}
                      {ligne.deja > 0 ? ` · déjà ${formatVolume(ligne.deja)}` : ''}
                    </p>
                  ) : null}
                </div>
                <div className="space-y-1">
                  <Label>Dépoté (L)</Label>
                  <Input
                    value={ligne.saisie}
                    onChange={(e) => majLigneLivraison(index, e.target.value)}
                  />
                </div>
                {/* Le camion qui garde du volume ne passera pas « livré »: il
                    reste disponible pour un autre client, et l'écran le dit
                    avant que l'exploitant ne valide plutôt qu'après. */}
                {ligne.trip_id &&
                Number(ligne.saisie || 0) > 0 &&
                Number(ligne.saisie || 0) < ligne.aDepoter ? (
                  <p className="mt-2 text-xs text-flame-ink">
                    Il restera {formatVolume(ligne.aDepoter - Number(ligne.saisie || 0))}{' '}
                    sur ce camion : il reste disponible pour une autre commande.
                  </p>
                ) : null}
              </div>
            ))}
          </div>
          <DialogFooter className="border-t bg-card px-6 py-4">
            <Button
              variant="outline"
              onClick={() => setShowDeliveryModal(false)}
            >
              Annuler
            </Button>
            <Button onClick={handleDelivery} disabled={savingDelivery}>{savingDelivery ? 'Enregistrement…' : 'Enregistrer'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrdersPage;
